import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Building2, User, Mail, Phone, MapPin, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';

function PasswordStrengthHint({ password, t }) {
  if (!password) return null;
  const hasMinLength = password.length >= 8;
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasDigit = /[0-9]/.test(password);

  const rules = [
    { key: 'minLength', ok: hasMinLength, label: t('registerCompany.pwMin8') },
    { key: 'letter', ok: hasLetter, label: t('registerCompany.pwLetter') },
    { key: 'digit', ok: hasDigit, label: t('registerCompany.pwDigit') },
  ];

  return (
    <div className="mt-2 space-y-1">
      {rules.map(r => (
        <div key={r.key} className={`flex items-center gap-2 text-xs ${r.ok ? 'text-emerald-400' : 'text-rose-400'}`}>
          {r.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
          {r.label}
        </div>
      ))}
    </div>
  );
}

export default function RegisterCompany() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    admin_name: '',
    admin_email: '',
    admin_password: ''
  });
  const [sameEmail, setSameEmail] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentPrivacy, setConsentPrivacy] = useState(false);
  const { registerCompany } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const isPasswordValid = () => {
    const pw = formData.admin_password;
    return pw.length >= 8 && /[a-zA-Z]/.test(pw) && /[0-9]/.test(pw);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isPasswordValid()) {
      setError(t('registerCompany.pwInvalid'));
      return;
    }
    if (!consentTerms || !consentPrivacy) {
      setError(t('registerCompany.consentRequired'));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        admin_email: sameEmail ? formData.email : formData.admin_email
      };
      await registerCompany(payload);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || t('registerCompany.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4" data-testid="register-company-page">
      {/* Language Switcher - Top Right */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher variant="auth" />
      </div>

      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-wp-medium/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-lg relative">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center mb-4">
            <span className="text-4xl font-light tracking-wide text-white font-heading">WORK</span>
            <span className="text-4xl font-light tracking-wide text-wp-light font-heading">+</span>
          </div>
          <p className="text-wp-light/40">{t('registerCompany.title')}</p>
        </div>

        {/* Form */}
        <div className="glass-card rounded-2xl p-8">
          <div className="flex items-center gap-2 mb-6">
            <span className="text-emerald-400 text-sm font-medium">✨ {t('registerCompany.freeTrial')}</span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm" data-testid="register-company-error">
                {error}
              </div>
            )}

            {/* Company Info */}
            <div className="space-y-4">
              <h3 className="text-white font-medium flex items-center gap-2">
                <Building2 className="h-4 w-4 text-wp-light" />
                {t('registerCompany.companyData')}
              </h3>

              <div className="space-y-2">
                <Label htmlFor="name" className="text-wp-light/70">{t('registerCompany.companyName')}</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('registerCompany.companyNamePlaceholder')}
                  required
                  data-testid="company-name-input"
                  className="h-12 bg-wp-dark/30 border-wp-dark/50 text-white placeholder:text-white/30 rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-wp-light/70">{t('registerCompany.companyEmail')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-white/30" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="info@company.com"
                    required
                    data-testid="company-email-input"
                    className="h-12 bg-wp-dark/30 border-wp-dark/50 text-white placeholder:text-white/30 rounded-xl pl-10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-wp-light/70">{t('registerCompany.phone')}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-white/30" />
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t('registerCompany.phonePlaceholder')}
                      data-testid="company-phone-input"
                      className="h-12 bg-wp-dark/30 border-wp-dark/50 text-white placeholder:text-white/30 rounded-xl pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address" className="text-wp-light/70">{t('registerCompany.address')}</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-white/30" />
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder={t('registerCompany.addressPlaceholder')}
                      data-testid="company-address-input"
                      className="h-12 bg-wp-dark/30 border-wp-dark/50 text-white placeholder:text-white/30 rounded-xl pl-10"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Admin Info */}
            <div className="space-y-4 pt-4 border-t border-wp-dark/50">
              <h3 className="text-white font-medium flex items-center gap-2">
                <User className="h-4 w-4 text-wp-light" />
                {t('registerCompany.adminData')}
              </h3>

              <div className="space-y-2">
                <Label htmlFor="admin_name" className="text-wp-light/70">{t('registerCompany.adminName')}</Label>
                <Input
                  id="admin_name"
                  name="admin_name"
                  value={formData.admin_name}
                  onChange={handleChange}
                  placeholder={t('registerCompany.adminNamePlaceholder')}
                  required
                  data-testid="admin-name-input"
                  className="h-12 bg-wp-dark/30 border-wp-dark/50 text-white placeholder:text-white/30 rounded-xl"
                />
              </div>

              {/* Admin email */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-wp-light/70">{t('registerCompany.adminEmail') || 'Admin email'}</Label>
                  <label className="flex items-center gap-2 text-xs text-wp-light/50 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={sameEmail}
                      onChange={e => setSameEmail(e.target.checked)}
                      className="h-3.5 w-3.5 rounded accent-indigo-500"
                    />
                    {t('registerCompany.sameAsCompany') || 'Same as company email'}
                  </label>
                </div>
                {!sameEmail && (
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-white/30" />
                    <Input
                      id="admin_email"
                      name="admin_email"
                      type="email"
                      value={formData.admin_email}
                      onChange={handleChange}
                      placeholder="admin@company.com"
                      required
                      className="h-12 bg-wp-dark/30 border-wp-dark/50 text-white placeholder:text-white/30 rounded-xl pl-10"
                    />
                  </div>
                )}
                {sameEmail && (
                  <div className="h-10 flex items-center px-3 rounded-xl bg-wp-dark/20 border border-wp-dark/30 text-wp-light/40 text-sm">
                    {formData.email || 'info@company.com'}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin_password" className="text-wp-light/70">{t('registerCompany.password')}</Label>
                <div className="relative">
                  <Input
                    id="admin_password"
                    name="admin_password"
                    type={showPassword ? "text" : "password"}
                    value={formData.admin_password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    data-testid="admin-password-input"
                    className="h-12 bg-wp-dark/30 border-wp-dark/50 text-white placeholder:text-white/30 rounded-xl pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-wp-light/50 hover:text-wp-lightest/80 transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <PasswordStrengthHint password={formData.admin_password} t={t} />
              </div>
            </div>

            {/* Legal Consent */}
            <div className="space-y-3 pt-4 border-t border-wp-dark/50">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={consentTerms}
                  onChange={e => setConsentTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-wp-dark/50 accent-indigo-500"
                  data-testid="consent-terms"
                />
                <span className="text-sm text-wp-light/60 leading-relaxed">
                  {t('registerCompany.agreeTerms')}{' '}
                  <Link to="/terms" target="_blank" className="text-wp-light hover:text-white underline underline-offset-2">
                    {t('registerCompany.termsLink')}
                  </Link>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={consentPrivacy}
                  onChange={e => setConsentPrivacy(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-wp-dark/50 accent-indigo-500"
                  data-testid="consent-privacy"
                />
                <span className="text-sm text-wp-light/60 leading-relaxed">
                  {t('registerCompany.agreePrivacy')}{' '}
                  <Link to="/privacy" target="_blank" className="text-wp-light hover:text-white underline underline-offset-2">
                    {t('registerCompany.privacyLink')}
                  </Link>
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="register-company-submit"
              className="w-full h-12 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-wp-medium hover:to-violet-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-indigo-500/30 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Building2 className="h-5 w-5" />
                  {t('registerCompany.createAccount')}
                </>
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-wp-light/40 text-sm">
            {t('registerCompany.haveAccount')}{' '}
            <Link to="/login" className="text-wp-light hover:text-wp-lightest transition-colors" data-testid="login-link">
              {t('registerCompany.signIn')}
            </Link>
          </p>
        </div>

        {/* Trial info */}
        <div className="mt-6 glass-card rounded-xl p-4 text-center">
          <p className="text-wp-light/60 text-sm">
            {t('registerCompany.trialInfo')}
          </p>
        </div>
      </div>
    </div>
  );
}
